import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  CircleDollarSign,
  Clock,
  ExternalLink,
  FileText,
  Globe,
  Landmark,
  Mail,
  MapPin,
  Phone
} from "lucide-react";
import {
  arquivarServicoAdmin,
  atualizarHomeConfigAdmin,
  atualizarServicoAdmin,
  buscarContextoAdmin,
  buscarHomeConfigAdmin,
  buscarHomeConfigPublico,
  buscarServicoAdmin,
  buscarServicoPublico,
  criarServicoAdmin,
  despublicarServicoAdmin,
  listarAuditoriaServicoAdmin,
  listarServicosAdmin,
  listarServicosPublicos,
  listarRevisoesServicoAdmin,
  publicarServicoAdmin,
  restaurarRevisaoServicoAdmin
} from "./api/servicosApi";
import {
  PERMISSAO_ARQUIVAR,
  PERMISSAO_DESPUBLICAR,
  PERMISSAO_EDITAR,
  PERMISSAO_PUBLICAR,
  PERMISSAO_RESTAURAR_REVISAO
} from "./auth";
import tstLogo from "./assets/logo_tst.png";
import AccessManagement from "./components/AccessManagement";
import {
  GovBrButton,
  GovBrInput,
  GovBrSelect,
  GovBrTextarea
} from "./components/GovBrControls";
import { ServiceIcon } from "./components/ServiceIcons";
import { KEYCLOAK_ENABLED } from "./env";
import {
  initializeGoogleAnalytics,
  trackGoogleAnalyticsPageView
} from "./shared/googleAnalytics";
import { initializeHotjar, trackHotjarPageView } from "./shared/hotjar";
import { getServicePath, getServiceSlug } from "./shared/serviceNavigation";
import "./index.css";

const IFRAME_HEIGHT_MESSAGE_TYPE = "PORTAL_SERVICOS_HEIGHT";
const IFRAME_NAVIGATION_MESSAGE_TYPE = "PORTAL_SERVICOS_NAVIGATION";
const IFRAME_APP_ID = "portal-servicos-tst";
const MIN_IFRAME_HEIGHT = 600;
export const ADMIN_PATH = "/gestao-admin";
export const ACCESS_ADMIN_PATH = `${ADMIN_PATH}/acessos`;
let iframeHeightFrame = null;

const TST_HOME_URL = "https://www.tst.jus.br/";
const CARTA_SERVICOS_URL = "https://www.tst.jus.br/en/web/acesso-a-informacao/carta-de-servicos-a-cidadania";
const HOME_ANNOUNCEMENT_TEXT = "Em breve, novos serviços serão adicionados. Enquanto isso, envie sua opinião e ajude a melhorar o portal de serviços do TST.";
const sectionTypeLabels = {
  TEXTO: "Texto",
  ETAPAS: "Etapas",
  ALERTA: "Alerta",
  LINKS: "Links",
  ANEXOS: "Anexos",
  CONTATO: "Contato"
};

const actionTypeLabels = {
  LINK_EXTERNO: "Link externo",
  LINK_INTERNO: "Link interno",
  FORMULARIO_INTERNO: "Formulário interno",
  COMPONENTE_CUSTOMIZADO: "Componente customizado"
};

function adminStatusLabel(service) {
  if (service?.alteracoesPendentes) {
    return "PUBLICADO COM ALTERAÇÕES PENDENTES";
  }
  return service?.status || "RASCUNHO";
}

const detailServiceConfig = {
  "emitir-certidao-andamento": {
    mode: "emitir",
    lastUpdated: "01/07/2026",
    metadata: { documento: "CNPJ", prazo: "imediato", custo: "gratuito" },
    whatIs: {
      type: "paragraphs",
      paragraphs: [
        "Use esse serviço para gerar uma certidão que lista os processos de uma empresa que estão no Tribunal Superior do Trabalho (TST)."
      ]
    },
    steps: {
      title: "1. Emitir certidão",
      bullets: ["Escolha um dos canais de atendimento abaixo", "Informe o CNPJ que deseja consultar"],
      webInstruction: "Siga as orientações do sistema para emitir e baixar a certidão"
    },
    otherInfo: "emitir-andamento",
    relatedValidationLabel: "Validar certidão de processos em andamento no TST",
    relatedEmissionLabel: "Emitir Certidão de Processos Arquivados no TST ou Devolvidos ao TRT de Origem"
  },
  "validar-certidao-andamento": {
    mode: "validar",
    lastUpdated: "01/07/2026",
    metadata: { documento: "Código de autenticidade da certidão", prazo: "imediato", custo: "gratuito" },
    whatIs: {
      type: "validation",
      certLabel: "Certidão de Processos em Andamento no TST",
      paragraphs: [
        "A validação diz se o documento é uma certidão emitida pelo TST ou não.",
        "A validação confirma se o documento apresentado corresponde a uma certidão emitida pelo TST, e apresenta uma cópia da certidão original para conferência."
      ]
    },
    steps: {
      title: "1. Validar certidão",
      bullets: ["Acesse o canal abaixo", "Informe o código de autenticidade que aparece na certidão"],
      webInstruction: "Siga as orientações do sistema para validar a certidão"
    },
    otherInfo: "validar-andamento",
    relatedEmissionLabel: "Emitir Certidão de Processos em Andamento no TST"
  },
  "emitir-certidao-arquivados": {
    mode: "emitir",
    lastUpdated: "01/07/2026",
    metadata: { documento: "CNPJ", prazo: "imediato", custo: "gratuito" },
    whatIs: {
      type: "archived-emission"
    },
    steps: {
      title: "1. Emitir certidão",
      bullets: ["Escolha um dos canais de atendimento abaixo", "Informe o CNPJ que deseja consultar"],
      webInstruction: "Siga as orientações do sistema para emitir e baixar a certidão"
    },
    otherInfo: "emitir-arquivados",
    relatedValidationLabel: "Validar certidão de processos arquivados no TST ou devolvidos ao TRT de origem",
    relatedEmissionLabel: "Emitir Certidão de Processos em Andamento no TST"
  },
  "validar-certidao-arquivados": {
    mode: "validar",
    lastUpdated: "01/07/2026",
    metadata: { documento: "Código de autenticidade da certidão", prazo: "imediato", custo: "gratuito" },
    whatIs: {
      type: "validation",
      certLabel: "Certidão de processos arquivados no TST ou devolvidos ao TRT de origem",
      paragraphs: []
    },
    steps: {
      title: "1. Validar certidão",
      bullets: ["Acesse o canal abaixo", "Informe o código de autenticidade que aparece na certidão"],
      webInstruction: "Siga as orientações do sistema para validar a certidão"
    },
    otherInfo: "validar-arquivados",
    relatedEmissionLabel: "Emitir Certidão de processos arquivados no TST ou devolvidos ao TRT de origem"
  }
};

function usesStructuredItems(type) {
  return ["LINKS", "ANEXOS"].includes(type);
}

function structuredItemLabel(type) {
  return type === "ANEXOS" ? "anexo" : "link";
}

function createEmptyStep() {
  return {
    nome: "",
    descricao: "",
    canal: "",
    documentos: "",
    duracao: "",
    custos: "",
    requisitos: "",
    acompanhamento: ""
  };
}

function createEmptyLink() {
  return {
    tipo: "LINK_EXTERNO",
    rotulo: "",
    url: "",
    componente: "",
    descricao: "",
    abrirNovaAba: true
  };
}

function createSection(type = "TEXTO", titulo = "") {
  return {
    id: "",
    tipo: type,
    titulo: titulo || sectionTypeLabels[type] || "Seção",
    texto: "",
    etapas: type === "ETAPAS" ? [createEmptyStep()] : [],
    links: usesStructuredItems(type) ? [createEmptyLink()] : []
  };
}

function createDefaultSections() {
  return [
    { ...createSection("TEXTO", "O que é?"), id: "o-que-e" },
    { ...createSection("TEXTO", "Quem pode utilizar o serviço?"), id: "quem-pode-usar" },
    { ...createSection("ETAPAS", "Etapas para a realização deste serviço"), id: "etapas" },
    { ...createSection("TEXTO", "Outras informações"), id: "outras-informacoes" }
  ];
}

function createEmptyForm() {
  return {
    id: null,
    slug: "",
    titulo: "",
    conhecidoComo: "",
    resumo: "",
    icone: "Files",
    categoria: "Serviços",
    ordem: "",
    ativo: false,
    status: "RASCUNHO",
    alteracoesPendentes: false,
    permissoes: [],
    acaoTipo: "LINK_EXTERNO",
    acaoRotulo: "Iniciar",
    acaoUrl: "",
    acaoComponente: "",
    abrirNovaAba: true,
    secoes: createDefaultSections(),
    contatoUnidade: "",
    contatoSuporteTecnico: "",
    contatoSuporteNegocial: "",
    contatoEmail: "",
    contatoEndereco: "",
    contatoHorario: "",
    tags: ""
  };
}

const EMPTY_FORM = createEmptyForm();

function normalize(value = "") {
  return String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function searchableText(value) {
  if (value == null) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.map(searchableText).join(" ");
  }
  if (typeof value === "object") {
    return Object.values(value).map(searchableText).join(" ");
  }
  return String(value);
}

function TstLogo() {
  return (
    <img
      className="tst-logo"
      src={tstLogo}
      alt="Justiça do Trabalho - Tribunal Superior do Trabalho"
    />
  );
}

function PortalHeader({ titleRef = null }) {
  return (
    <header className="portal-hero-header">
      <div className="portal-hero-header__content">
        <a className="portal-hero-header__logo-link" href={TST_HOME_URL} target="_blank" rel="noreferrer" aria-label="Acessar o portal do Tribunal Superior do Trabalho">
          <TstLogo />
        </a>
        <span className="portal-hero-header__divider" aria-hidden="true" />
        <h1 ref={titleRef} tabIndex="-1">Portal de Serviços do TST</h1>
      </div>
    </header>
  );
}

function LoadingState({ label = "Carregando serviços..." }) {
  return (
    <div className="portal-state" role="status">
      <br-loading
        density="medium"
        label={label}
      ></br-loading>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="portal-state portal-state--error">
      <br-message
        message={message}
        message-title="Não foi possível carregar o Portal de Serviços."
        show-icon="true"
        state="danger"
      ></br-message>
      {onRetry && (
        <GovBrButton onClick={onRetry}>
          Tentar novamente
        </GovBrButton>
      )}
    </div>
  );
}

function parseLinks(text = "") {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [label, url] = line.split("|").map((part) => part?.trim());
      return { tipo: "LINK_EXTERNO", rotulo: label || url, url: url || label, componente: "", abrirNovaAba: true };
    });
}

function inferActionType(item = {}) {
  if (!item) {
    return "LINK_EXTERNO";
  }
  if (item.tipo) {
    return item.tipo;
  }
  if (item.componente) {
    return "COMPONENTE_CUSTOMIZADO";
  }
  if (item.url?.startsWith("http") || item.url?.startsWith("mailto:")) {
    return "LINK_EXTERNO";
  }
  return item.url ? "LINK_INTERNO" : "LINK_EXTERNO";
}

function findSectionLink(section, keyOrLabel) {
  const normalizedKey = normalize(keyOrLabel);
  return (section?.links || []).find((link) => (
    normalize(link.descricao) === normalizedKey ||
    normalize(link.rotulo) === normalizedKey ||
    normalize(link.url) === normalizedKey ||
    normalize(link.componente) === normalizedKey
  ));
}

function findServiceLink(service, keyOrLabel) {
  for (const section of service?.secoes || []) {
    const link = findSectionLink(section, keyOrLabel);
    if (link) {
      return link;
    }
  }
  return null;
}

function actionToLink(action = {}, fallbackLabel = "Iniciar") {
  const safeAction = action || {};
  return {
    tipo: safeAction.tipo || "LINK_EXTERNO",
    rotulo: safeAction.rotulo || fallbackLabel,
    url: safeAction.url || "",
    componente: safeAction.componente || "",
    abrirNovaAba: safeAction.abrirNovaAba !== false
  };
}

function ServicesList({ services, onSelect }) {
  const selectCard = (event, slug) => {
    if (event.type === "keydown" && !["Enter", " "].includes(event.key)) {
      return;
    }
    if (event.type === "keydown") {
      event.preventDefault();
    }
    onSelect(slug);
  };

  return (
    <div className="services-grid" id="lista-servicos-publicos">
      {services.map((service) => (
        <br-card
          interactive="true"
          key={service.slug}
          role="button"
          tabIndex={0}
          onClick={(event) => selectCard(event, service.slug)}
          onKeyDown={(event) => selectCard(event, service.slug)}
        >
          <span className="service-card__body" slot="content">
            <span className="service-card__icon">
              <ServiceIcon serviceId={service.slug} />
            </span>
            <span className="service-card__content">
              <strong>{service.titulo}</strong>
              <span>{service.resumo}</span>
            </span>
          </span>
        </br-card>
      ))}
    </div>
  );
}

function formatDate(value) {
  if (!value) {
    return "01/07/2026";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "01/07/2026";
  }
  return new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo" }).format(date);
}

function DetailIcon({ type }) {
  const icons = {
    building: Landmark,
    clock: Clock,
    cost: CircleDollarSign,
    document: FileText,
    globe: Globe,
    location: MapPin,
    mail: Mail,
    phone: Phone
  };
  const Icon = icons[type] || CircleDollarSign;
  const channelClass = ["globe", "location"].includes(type) ? " detail-icon--channel" : "";
  return <Icon className={`detail-icon${channelClass}`} aria-hidden="true" />;
}

function getPrimaryStep(service) {
  return (service?.secoes || [])
    .flatMap((section) => section?.etapas || [])
    .find((step) => step?.nome || step?.descricao) || {};
}

function getDocumentLabel(service) {
  const document = getPrimaryStep(service).documentos?.[0];
  if (document) {
    return document;
  }
  return service?.slug?.startsWith("validar") ? "Código de autenticidade" : "CNPJ";
}

function renderInlineText(text, links = [], onNavigateService, onRunComponent) {
  const link = (links || []).find((candidate) => candidate?.rotulo && text.includes(candidate.rotulo));
  const parts = link ? text.split(link.rotulo) : [text];

  const renderFormatted = (value, baseKey) => {
    const nodes = [];
    const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let lastIndex = 0;
    let match;
    while ((match = pattern.exec(value)) !== null) {
      if (match.index > lastIndex) {
        nodes.push(value.slice(lastIndex, match.index));
      }
      const token = match[0];
      const content = token.replace(/^\*\*?|\*\*?$/g, "");
      nodes.push(token.startsWith("**")
        ? <strong key={`${baseKey}-strong-${match.index}`}>{content}</strong>
        : <em key={`${baseKey}-em-${match.index}`}>{content}</em>);
      lastIndex = pattern.lastIndex;
    }
    if (lastIndex < value.length) {
      nodes.push(value.slice(lastIndex));
    }
    return nodes;
  };

  return parts.flatMap((part, index) => {
    const nodes = renderFormatted(part, `part-${index}`);
    if (!link || index === parts.length - 1) {
      return nodes;
    }
    return [
      ...nodes,
      <DynamicLink
        key={`${link.rotulo}-${index}`}
        link={link}
        onNavigateService={onNavigateService}
        onRunComponent={onRunComponent}
      >
        {link.rotulo}
      </DynamicLink>
    ];
  });
}

function normalizeRichLine(line) {
  return line
    .replace(/^[💲$]\s*/, "")
    .replace(/^🕔\s*/, "")
    .replace(/^📄\s*/, "");
}

function RichText({ text = "", links = [], onNavigateService, onRunComponent }) {
  const blocks = [];
  let list = null;

  const flushList = () => {
    if (list?.items?.length) {
      blocks.push({ ...list });
    }
    list = null;
  };

  String(text || "").split("\n").forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) {
      flushList();
      return;
    }

    if (line.startsWith("### ")) {
      flushList();
      blocks.push({ type: "heading", text: normalizeRichLine(line.replace(/^###\s+/, "")) });
      return;
    }

    if (line.startsWith("* ")) {
      if (!list || list.type !== "ul") {
        flushList();
        list = { type: "ul", items: [] };
      }
      list.items.push(normalizeRichLine(line.replace(/^\*\s+/, "")));
      return;
    }

    if (/^\d+\.\s+/.test(line)) {
      if (!list || list.type !== "ol") {
        flushList();
        list = { type: "ol", items: [] };
      }
      list.items.push(normalizeRichLine(line.replace(/^\d+\.\s+/, "")));
      return;
    }

    flushList();
    const normalizedLine = normalizeRichLine(line);
    const metadataMatch = normalizedLine.replace(/\*\*/g, "").match(/^(Custo|Prazo|Documento necessário|Informação necessária|Informação Necessária):\s*(.+)$/i);
    if (metadataMatch) {
      blocks.push({ type: "metadata", label: metadataMatch[1], text: metadataMatch[2] });
      return;
    }
    blocks.push({ type: "paragraph", text: normalizedLine });
  });

  flushList();

  return (
    <div className="rich-service-text">
      {blocks.map((block, index) => {
        if (block.type === "heading") {
          return <h3 key={`${block.type}-${index}`}>{renderInlineText(block.text, links, onNavigateService, onRunComponent)}</h3>;
        }
        if (block.type === "ul") {
          return (
            <ul key={`${block.type}-${index}`}>
              {block.items.map((item, itemIndex) => <li key={`${item}-${itemIndex}`}>{renderInlineText(item, links, onNavigateService, onRunComponent)}</li>)}
            </ul>
          );
        }
        if (block.type === "ol") {
          return (
            <ol key={`${block.type}-${index}`}>
              {block.items.map((item, itemIndex) => <li key={`${item}-${itemIndex}`}>{renderInlineText(item, links, onNavigateService, onRunComponent)}</li>)}
            </ol>
          );
        }
        if (block.type === "metadata") {
          const iconType = normalize(block.label).includes("prazo")
            ? "clock"
            : normalize(block.label).includes("informacao") || normalize(block.label).includes("documento")
              ? "document"
              : "cost";
          return (
            <p key={`${block.type}-${index}`} className="rich-service-text__meta">
              <DetailIcon type={iconType} /> {block.label}: <strong>{renderInlineText(block.text, links, onNavigateService, onRunComponent)}</strong>
            </p>
          );
        }
        return <p key={`${block.type}-${index}`}>{renderInlineText(block.text, links, onNavigateService, onRunComponent)}</p>;
      })}
    </div>
  );
}

function DynamicLink({
  link,
  children,
  className = "text-link",
  inlineAction = false,
  onNavigateService,
  onRunComponent
}) {
  const label = children || link?.rotulo;

  if (!link) {
    return <span className={className}>{label}</span>;
  }

  const type = inferActionType(link);

  if (inlineAction && type !== "LINK_EXTERNO") {
    const handleClick = (event) => {
      event.preventDefault();
      if (type === "LINK_INTERNO") {
        onNavigateService?.(link.url);
        return;
      }
      onRunComponent?.(link);
    };

    return (
      <br-link
        href={link.url || `#${link.componente || "servico"}`}
        onClick={handleClick}
      >
        {label}
      </br-link>
    );
  }

  if (type === "LINK_INTERNO") {
    return (
      <GovBrButton emphasis="tertiary" onClick={() => onNavigateService?.(link.url)}>
        {label}
      </GovBrButton>
    );
  }

  if (type === "FORMULARIO_INTERNO" || type === "COMPONENTE_CUSTOMIZADO") {
    return (
      <GovBrButton emphasis="tertiary" onClick={() => onRunComponent?.(link)}>
        {label}
      </GovBrButton>
    );
  }

  return (
    <a
      className={className}
      href={link.url}
      target={link.abrirNovaAba === false || link.url?.startsWith("mailto:") ? undefined : "_blank"}
      rel={link.abrirNovaAba === false || link.url?.startsWith("mailto:") ? undefined : "noreferrer"}
    >
      {label}
    </a>
  );
}

function StartAction({ action, preview = false, compact = false, onRunComponent, children }) {
  const link = actionToLink(action);
  const type = inferActionType(link);
  const label = children || link.rotulo || "Iniciar";
  const className = `service-start-action${compact ? " service-start-action--compact" : ""}`;

  if (preview) {
    return (
      <button type="button" className={className} disabled title="Ação desabilitada no preview administrativo">
        {label}
      </button>
    );
  }

  if (type === "LINK_EXTERNO") {
    return (
      <a
        className={className}
        href={link.url}
        target={link.abrirNovaAba === false ? undefined : "_blank"}
        rel={link.abrirNovaAba === false ? undefined : "noreferrer"}
      >
        {label}
      </a>
    );
  }

  return (
    <button type="button" className={className} onClick={() => onRunComponent?.(link)}>
      {label}
    </button>
  );
}

function ChannelCards({ section, service, config, onNavigateService, onRunComponent }) {
  const requestLink = findSectionLink(section, "solicitacao-web") || findSectionLink(section, "validacao-web") || actionToLink(service?.acaoPrincipal);
  const addressLink = findSectionLink(section, "endereco-tst");
  const step = section?.etapas?.[0] || getPrimaryStep(service);
  const isValidation = config?.mode === "validar" || service?.slug?.startsWith("validar");
  const hasPresentialChannel = !isValidation && normalize(step?.canal).includes("presencial");
  const pageLinkLabel = isValidation ? "página de validação desta certidão" : "página de solicitação desta certidão";
  const webInstruction = isValidation
    ? "Siga as orientações do sistema para validar a certidão"
    : "Siga as orientações do sistema para emitir e baixar a certidão";
  const channelTitle = isValidation ? "Portal Web (Internet):" : service?.slug === "emitir-certidao-andamento" ? "Portal Web (Internet)" : "Portal Web (Internet):";
  const monitoringText = step?.acompanhamento || (isValidation ? "" : "A certidão será emitida com um código de autenticidade.");

  return (
    <div className={`service-channel-grid${isValidation ? " service-channel-grid--single" : ""}`}>
      <article className="service-channel-card">
        <h4>
          <DetailIcon type="globe" />
          <span>{channelTitle}</span>
        </h4>
        <ol>
          <li>
            Acesse a{" "}
            <DynamicLink
              link={requestLink}
              inlineAction
              onNavigateService={onNavigateService}
              onRunComponent={onRunComponent}
            >
              {requestLink?.rotulo || pageLinkLabel}
            </DynamicLink>{" "}
            ou clique no botão{" "}
            <StartAction action={service?.acaoPrincipal} compact onRunComponent={onRunComponent}>Iniciar</StartAction>
          </li>
          <li>{config?.steps?.webInstruction || webInstruction}</li>
          <li>Lembre-se de clicar no campo “Não sou um robô”</li>
          {monitoringText && !isValidation && <li>{monitoringText}</li>}
        </ol>
        <div className="service-channel-card__meta">
          <p><DetailIcon type="cost" /> Custo: <strong>{step?.custos || "Gratuito"}</strong></p>
          <p><DetailIcon type="clock" /> Prazo: <strong>{step?.duracao || "Imediato"}</strong></p>
        </div>
        {isValidation && (
          <div className="service-channel-card__important">
            <p><strong>Importante:</strong></p>
            <ul>
              <li>Se o código for válido, o sistema abrirá a certidão original.</li>
              <li>Se o código não for válido, confira se o código foi digitado corretamente e tente novamente.</li>
            </ul>
          </div>
        )}
      </article>

      {hasPresentialChannel && (
        <article className="service-channel-card">
          <h4>
            <DetailIcon type="location" />
            <span>
              Presencial{" "}
              <DynamicLink link={addressLink} onNavigateService={onNavigateService} onRunComponent={onRunComponent}>
                {addressLink?.rotulo || "(Edifício Sede do TST)"}
              </DynamicLink>:
            </span>
          </h4>
          <ol>
            <li>
              Compareça ao <strong>Núcleo de Cadastramento Processual (NCP)</strong>:
              <ul>
                <li><strong>Endereço:</strong> Setor de Administração Federal Sul (SAFS) Q 8 Lote 1 - Asa Sul, Brasília - DF</li>
                <li><strong>Balcão do Protocolo:</strong> Bloco A, Térreo, sala AT-110</li>
                <li><strong>Atendimento:</strong> das 9h às 19h - segunda a sexta-feira</li>
              </ul>
            </li>
            <li>A certidão será emitida sem um código de autenticidade.</li>
          </ol>
          <div className="service-channel-card__meta">
            <p>
              <DetailIcon type="cost" /> Custo:{" "}
              <strong>
                Pode haver cobrança. Se precisar pagar, emita a{" "}
                <a href="https://gru.jt.jus.br/gru" target="_blank" rel="noreferrer">guia de pagamento GRU</a>.
                {" "}Consulte as regras em <em>Outras Informações</em>.
              </strong>
            </p>
            <p><DetailIcon type="clock" /> Prazo: <strong>até 15 dias úteis</strong></p>
          </div>
        </article>
      )}
    </div>
  );
}

function LegislationBlock({ links = [] }) {
  const normativeLink = links.find((link) => link.descricao === "instrucao-normativa-20");
  const jointActLink = links.find((link) => link.descricao === "ato-conjunto-21");
  const legislationLinks = [
    {
      link: normativeLink,
      label: normativeLink?.rotulo || "Instrução Normativa nº 20/2022",
      description: "Define casos em que a certidão será cobrada"
    },
    {
      link: jointActLink,
      label: jointActLink?.rotulo || "Ato Conjunto TST.CSJT.GP.SG nº 21/2010",
      description: "Define que o pagamento de taxas será por meio de Guia de Recolhimento da União (GRU)"
    }
  ].filter((item) => item.link);

  if (legislationLinks.length === 0) {
    return null;
  }

  return (
    <div className="legislation-block">
      <h3>Legislação</h3>
      <ul>
        {legislationLinks.map((item) => (
          <li key={item.label}>
            <DynamicLink link={item.link}>{item.label}</DynamicLink>
            {" "} - {item.description}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Section({ section, service, defaultOpen = false, onNavigateService, onRunComponent }) {
  const config = detailServiceConfig[service?.slug];
  const isSteps = section.tipo === "ETAPAS";

  return (
    <article className="service-section">
      <br-collapse
        divider="true"
        open={defaultOpen}
      >
        <span slot="trigger">{section.titulo}</span>
        <div className="service-section__body">
          {isSteps ? (
            <div className="steps-detail">
              {(section.etapas || []).map((step, index) => (
                <div key={`${step.nome}-${index}`}>
                  <h3>{index + 1}. {step.nome}</h3>
                  {step.descricao && <RichText text={step.descricao} links={section.links} onNavigateService={onNavigateService} onRunComponent={onRunComponent} />}
                </div>
              ))}
              <h3>Canais de atendimento</h3>
              <ChannelCards section={section} service={service} config={config} onNavigateService={onNavigateService} onRunComponent={onRunComponent} />
            </div>
          ) : usesStructuredItems(section.tipo) ? (
            <div className={`section-links-list${section.tipo === "ANEXOS" ? " section-links-list--attachments" : ""}`}>
              {((section.links || []).length > 0 ? section.links : parseLinks(section.texto)).map((link, index) => (
                <DynamicLink
                  link={link}
                  key={`${link.rotulo}-${index}`}
                  className="section-links-list__item"
                  onNavigateService={onNavigateService}
                  onRunComponent={onRunComponent}
                >
                  <span>
                    {link.rotulo}
                    {section.tipo === "ANEXOS" && <em>Anexo</em>}
                    {link.descricao && <small>{link.descricao}</small>}
                  </span>
                  <ExternalLink className="section-link-icon" aria-hidden="true" />
                </DynamicLink>
              ))}
            </div>
          ) : section.tipo === "ALERTA" ? (
            <div className="section-alert">{section.texto}</div>
          ) : section.tipo === "CONTATO" ? (
            <pre className="section-contact-text">{section.texto}</pre>
          ) : (
            <div className="rich-service-text">
              <RichText text={section.texto} links={section.links} onNavigateService={onNavigateService} onRunComponent={onRunComponent} />
              {section.id === "outras-informacoes" && <LegislationBlock links={section.links} />}
              {(section.links || []).length > 0 && (
                <div className="section-inline-links">
                  {(section.links || [])
                    .filter((link) => !["ouvidoria", "instrucao-normativa-20", "ato-conjunto-21"].includes(link.descricao) && !section.texto?.includes(link.rotulo))
                    .map((link, index) => (
                      <DynamicLink
                        key={`${link.rotulo}-${index}`}
                        link={link}
                        onNavigateService={onNavigateService}
                        onRunComponent={onRunComponent}
                      >
                        {link.rotulo}
                      </DynamicLink>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      </br-collapse>
    </article>
  );
}

function HelpPanel({ service, onNavigateService, onRunComponent }) {
  const ouvidoriaLink = findServiceLink(service, "ouvidoria");
  const contato = service?.contatoAjuda || {};
  const legalSupportTitle = service?.slug?.startsWith("validar")
    ? "Dúvidas sobre validar certidão (Suporte Jurídico)"
    : "Dúvidas sobre emitir certidão (Suporte Jurídico)";
  return (
    <>
      <article className="service-section help-section">
        <br-collapse
          divider="true"
          open={false}
        >
          <span slot="trigger">Precisa de ajuda?</span>
          <div className="service-section__body" id="aba-suporte-ajuda">
            <div className="help-card-grid">
              <div className="help-contact-card">
                <h3>{legalSupportTitle}</h3>
                <p><DetailIcon type="mail" /> <strong>E-mail:</strong> <a href={`mailto:${contato.email || "ncp@tst.jus.br"}`}>{contato.email || "ncp@tst.jus.br"}</a></p>
                <p><DetailIcon type="phone" /> <strong>Telefone:</strong> {contato.suporteNegocial || "(61) 3043-4330, das 9h às 18h"}</p>
                <p><DetailIcon type="building" /> <strong>Área responsável:</strong> {contato.unidade || "Núcleo de Cadastramento Processual (NCP)"}</p>
              </div>
              <div className="help-contact-card">
                <h3>Problemas no sistema (Suporte Técnico)</h3>
                <p><DetailIcon type="mail" /> <strong>E-mail:</strong> <a href="mailto:suporte@tst.jus.br">suporte@tst.jus.br</a></p>
                <p><DetailIcon type="phone" /> <strong>Telefone:</strong> {contato.suporteTecnico || "(61) 3043-4040, das 9h às 19h"}</p>
                <p><DetailIcon type="building" /> <strong>Área responsável:</strong> Secretaria de Tecnologia da Informação e Comunicação (SETIN)</p>
              </div>
            </div>
          </div>
        </br-collapse>
      </article>

      <footer className="service-responsibility">
        <p>Conteúdo de responsabilidade da</p>
        <strong>Secretaria Geral Judiciária (SEGJUD)</strong>
        <p>Email: <a href="mailto:segjud@tst.jus.br">segjud@tst.jus.br</a></p>
        <p>Telefone: (61) 3043-3201 / 3043-7334</p>
        <hr />
        <p><strong>Fale com a Ouvidoria</strong></p>
        <p>
          Para{" "}
          <DynamicLink link={ouvidoriaLink} onNavigateService={onNavigateService} onRunComponent={onRunComponent}>
            registrar uma manifestação, reclamação, solicitação, sugestão ou denúncia
          </DynamicLink>
          .
        </p>
        <div className="service-feedback-copy">
          <strong>Ajude a melhorar este serviço</strong>
          <p>Deixe seu comentário no campo opinião nesta página.</p>
        </div>
      </footer>
    </>
  );
}

function HomeOuvidoriaFooter() {
  return (
    <footer className="home-ouvidoria-footer" id="ouvidoria-home-padrao">
      <p><strong>Fale com a Ouvidoria</strong></p>
      <p>
        Para{" "}
        <a href="https://www.tst.jus.br/en/faca-sua-manifestacao" target="_blank" rel="noreferrer">
          registrar uma manifestação, reclamação, solicitação, sugestão ou denúncia
        </a>
        .
      </p>
    </footer>
  );
}

function InternalServiceAction({ service, action, onBack }) {
  const isValidation = service?.slug?.startsWith("validar");
  const documentLabel = getDocumentLabel(service);
  return (
    <div className="service-detail internal-action-page">
      <GovBrButton emphasis="tertiary" onClick={onBack}>
        <ArrowLeft className="button-icon" aria-hidden="true" />
        Voltar ao serviço
      </GovBrButton>

      <section className="service-hero-card internal-action-card">
        <div>
          <span className="service-category-pill">{isValidation ? "Validação de certidão" : "Emissão de certidão"}</span>
          <h1>{service?.titulo}</h1>
          <p className="known-as">
            {isValidation
              ? "Informe o código de autenticidade da certidão para validar o documento."
              : `Informe ${documentLabel.toLowerCase().startsWith("número") ? "o" : "o"} ${documentLabel.toLowerCase()} que deseja consultar para iniciar a solicitação da certidão.`}
          </p>
        </div>
      </section>

      <article className="service-section">
        <div className="service-section__header service-section__header--static">
          <span>{isValidation ? "Dados para validação" : "Dados para emissão"}</span>
        </div>
        <div className="service-section__body">
          <div className="internal-action-form">
            <GovBrInput
              label={isValidation ? "Código de autenticidade" : documentLabel}
              placeholder={isValidation ? "Ex.: CERT-2026-000000" : documentLabel === "CPF" ? "Ex.: 000.000.000-00" : documentLabel === "Número do processo" ? "Ex.: 0000000-00.0000.0.00.0000" : "Ex.: 00.000.000/0001-00"}
            />
            <GovBrButton>
              <span aria-hidden="true">▶</span>
              {isValidation ? "Validar" : "Emitir"}
            </GovBrButton>
          </div>
          <p className="internal-action-note">
            Componente cadastrado: <strong>{action?.componente || action?.url || service?.slug || "certidão"}</strong>.
          </p>
        </div>
      </article>
    </div>
  );
}

function ServiceDetail({
  service,
  onBack,
  backLabel = "Voltar à página principal do Portal",
  preview = false,
  titleRef = null,
  onNavigateService,
  onRunComponent
}) {
  const action = service?.acaoPrincipal;
  const navigateToPortal = (event) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    onBack();
  };

  return (
    <div className="service-detail" id="detalhe-servico-publico">
      <nav className="service-breadcrumb" aria-label="Caminho de navegação">
        <ol>
          <li><a href={TST_HOME_URL}>Início</a></li>
          <li><a href="/" onClick={navigateToPortal}>Portal de Serviços</a></li>
          <li><a href="/" onClick={navigateToPortal}>{service.categoria || "Certidões"}</a></li>
          <li><span aria-current="page">{service.titulo}</span></li>
        </ol>
      </nav>

      <GovBrButton emphasis="tertiary" onClick={onBack}>
        <ArrowLeft className="button-icon" aria-hidden="true" />
        {backLabel}
      </GovBrButton>

      <section className="service-hero-card">
        <div>
          <h1 ref={titleRef} tabIndex="-1">{service.titulo}</h1>
          {service.conhecidoComo && (
            <p className="known-as">
              Também conhecido como: <em>{service.conhecidoComo}</em>
            </p>
          )}
        </div>

        <div className="start-action-wrap">
          {action && (
            <StartAction action={action} preview={preview} onRunComponent={onRunComponent} />
          )}
        </div>
      </section>

      <p className="last-updated">Última Modificação: {formatDate(service.atualizadoEm || service.publicadoEm)}</p>

      <div className="service-sections">
        {(service.secoes || []).map((section) => (
          <Section
            key={section.id || section.titulo}
            section={section}
            service={service}
            defaultOpen={section.id === "o-que-e"}
            onNavigateService={onNavigateService}
            onRunComponent={onRunComponent}
          />
        ))}
      </div>

      {service.contatoAjuda ? (
        <HelpPanel service={service} onNavigateService={onNavigateService} onRunComponent={onRunComponent} />
      ) : null}

    </div>
  );
}

function apiStepToForm(etapa = {}) {
  return {
    nome: etapa.nome || "",
    descricao: etapa.descricao || "",
    canal: etapa.canal || "",
    documentos: (etapa.documentos || []).join("\n"),
    duracao: etapa.duracao || "",
    custos: etapa.custos || "",
    requisitos: etapa.requisitos || "",
    acompanhamento: etapa.acompanhamento || ""
  };
}

function formStepToPayload(etapa = {}) {
  return {
    nome: etapa.nome,
    descricao: etapa.descricao,
    canal: etapa.canal,
    documentos: (etapa.documentos || "").split("\n").map((item) => item.trim()).filter(Boolean),
    duracao: etapa.duracao,
    custos: etapa.custos,
    requisitos: etapa.requisitos,
    acompanhamento: etapa.acompanhamento
  };
}

function apiLinkToForm(link = {}) {
  return {
    tipo: link.tipo || inferActionType(link),
    rotulo: link.rotulo || "",
    url: link.url || "",
    componente: link.componente || "",
    descricao: link.descricao || "",
    abrirNovaAba: link.abrirNovaAba !== false
  };
}

function formLinkToPayload(link = {}) {
  return {
    tipo: link.tipo || inferActionType(link),
    rotulo: link.rotulo,
    url: link.url,
    componente: link.componente,
    descricao: link.descricao,
    abrirNovaAba: link.abrirNovaAba !== false
  };
}

function hasText(value = "") {
  return Boolean(value?.trim());
}

function stepHasContent(step = {}) {
  return [
    step.nome,
    step.descricao,
    step.canal,
    step.documentos,
    step.duracao,
    step.custos,
    step.requisitos,
    step.acompanhamento
  ].some(hasText);
}

function linkHasContent(link = {}) {
  return [link.rotulo, link.url, link.componente, link.descricao].some(hasText);
}

function apiSectionToForm(section = {}, index = 0) {
  const tipo = section.tipo || "TEXTO";
  return {
    id: section.id || "",
    tipo,
    titulo: section.titulo || sectionTypeLabels[tipo] || `Seção ${index + 1}`,
    texto: section.texto || "",
    etapas: tipo === "ETAPAS"
      ? ((section.etapas || []).length > 0 ? section.etapas.map(apiStepToForm) : [createEmptyStep()])
      : [],
    links: (section.links || []).length > 0
      ? section.links.map(apiLinkToForm)
      : (usesStructuredItems(tipo) ? parseLinks(section.texto).map(apiLinkToForm) : [])
  };
}

function formSectionToPayload(section = {}, index = 0) {
  return {
    id: section.id,
    tipo: section.tipo || "TEXTO",
    titulo: section.titulo,
    ordem: index + 1,
    texto: section.tipo === "ETAPAS" || usesStructuredItems(section.tipo) ? null : section.texto,
    etapas: section.tipo === "ETAPAS"
      ? (section.etapas || []).filter(stepHasContent).map(formStepToPayload)
      : [],
    links: (section.links || [])
      .filter(linkHasContent)
      .map(formLinkToPayload)
  };
}

function validateForm(form, { publish = false } = {}) {
  const errors = [];
  if (!hasText(form.titulo)) {
    errors.push("Informe o título do serviço.");
  }
  if (!hasText(form.resumo)) {
    errors.push("Informe o resumo do serviço.");
  }

  const sections = form.secoes || [];
  if (sections.length === 0) {
    errors.push("Cadastre ao menos uma seção.");
  }

  sections.forEach((section, sectionIndex) => {
    const sectionLabel = `Seção ${sectionIndex + 1}`;
    if (!hasText(section.titulo)) {
      errors.push(`${sectionLabel}: informe o título da seção.`);
    }

    const filledLinks = (section.links || []).filter(linkHasContent);
    filledLinks.forEach((link, linkIndex) => {
      const requiresUrl = ["LINK_EXTERNO", "LINK_INTERNO"].includes(link.tipo || "LINK_EXTERNO");
      const requiresComponent = ["FORMULARIO_INTERNO", "COMPONENTE_CUSTOMIZADO"].includes(link.tipo);
      if (!hasText(link.rotulo) || (requiresUrl && !hasText(link.url)) || (requiresComponent && !hasText(link.componente) && !hasText(link.url))) {
        errors.push(`${sectionLabel}, link ${linkIndex + 1}: informe rótulo e destino.`);
      }
    });

    if (section.tipo === "ETAPAS") {
      const filledSteps = (section.etapas || []).filter(stepHasContent);
      filledSteps.forEach((step, stepIndex) => {
        if (!hasText(step.nome)) {
          errors.push(`${sectionLabel}, etapa ${stepIndex + 1}: informe o nome da etapa.`);
        }
      });
      if (publish && filledSteps.length === 0) {
        errors.push(`${sectionLabel}: cadastre ao menos uma etapa antes de publicar.`);
      }
      return;
    }

    if (usesStructuredItems(section.tipo)) {
      if (publish && filledLinks.length === 0) {
        errors.push(`${sectionLabel}: cadastre ao menos um ${structuredItemLabel(section.tipo)} antes de publicar.`);
      }
      return;
    }

    if (publish && !hasText(section.texto)) {
      errors.push(`${sectionLabel}: informe o texto da seção antes de publicar.`);
    }
  });

  return errors;
}

function formatValidationErrors(errors) {
  return errors.slice(0, 6).join(" ");
}

function adminTabForValidationErrors(errors = []) {
  const hasContentError = errors.some((error) => (
    error.startsWith("Seção ") || error === "Cadastre ao menos uma seção."
  ));
  return hasContentError ? "conteudo" : "geral";
}

function adminToForm(service) {
  if (!service) {
    return createEmptyForm();
  }
  const secoes = (service.secoes || []).length > 0
    ? service.secoes.map(apiSectionToForm)
    : createDefaultSections();
  return {
    ...createEmptyForm(),
    id: service.id,
    slug: service.slug || "",
    titulo: service.titulo || "",
    conhecidoComo: service.conhecidoComo || "",
    resumo: service.resumo || "",
    icone: service.icone || "Files",
    categoria: service.categoria || "Serviços",
    ordem: service.ordem ?? "",
    ativo: service.ativo !== false,
    status: service.status || "RASCUNHO",
    alteracoesPendentes: service.alteracoesPendentes === true,
    permissoes: service.permissoes || [],
    acaoTipo: service.acaoPrincipal?.tipo || "LINK_EXTERNO",
    acaoRotulo: service.acaoPrincipal?.rotulo || "Iniciar",
    acaoUrl: service.acaoPrincipal?.url || "",
    acaoComponente: service.acaoPrincipal?.componente || "",
    abrirNovaAba: service.acaoPrincipal?.abrirNovaAba !== false,
    secoes,
    contatoUnidade: service.contatoAjuda?.unidade || "",
    contatoSuporteTecnico: service.contatoAjuda?.suporteTecnico || "",
    contatoSuporteNegocial: service.contatoAjuda?.suporteNegocial || "",
    contatoEmail: service.contatoAjuda?.email || "",
    contatoEndereco: service.contatoAjuda?.endereco || "",
    contatoHorario: service.contatoAjuda?.horario || "",
    tags: (service.tags || []).join("\n")
  };
}

function formToPayload(form) {
  return {
    slug: form.slug,
    titulo: form.titulo,
    conhecidoComo: form.conhecidoComo,
    resumo: form.resumo,
    icone: form.icone,
    categoria: form.categoria,
    ordem: form.ordem === "" ? null : Number(form.ordem),
    acaoPrincipal: {
      tipo: form.acaoTipo,
      rotulo: form.acaoRotulo,
      url: form.acaoUrl,
      componente: form.acaoComponente,
      abrirNovaAba: form.abrirNovaAba
    },
    secoes: (form.secoes || []).map(formSectionToPayload),
    contatoAjuda: {
      unidade: form.contatoUnidade,
      suporteTecnico: form.contatoSuporteTecnico,
      suporteNegocial: form.contatoSuporteNegocial,
      email: form.contatoEmail,
      endereco: form.contatoEndereco,
      horario: form.contatoHorario
    },
    tags: form.tags.split("\n").map((item) => item.trim()).filter(Boolean)
  };
}

function formToPreviewService(form) {
  const payload = formToPayload(form);
  return {
    ...payload,
    id: form.id,
    status: form.status,
    slug: payload.slug || "preview",
    titulo: payload.titulo || "Serviço sem título",
    resumo: payload.resumo || "Resumo ainda não informado.",
    categoria: payload.categoria || "Serviços"
  };
}

function AdminField({ label, children, className = "" }) {
  const control = Array.isArray(children) ? children[0] : children;
  let govBrControl = control;

  if (control?.type === "input") {
    govBrControl = <GovBrInput label={label} {...control.props} />;
  } else if (control?.type === "textarea") {
    govBrControl = <GovBrTextarea label={label} {...control.props} />;
  } else if (control?.type === "select") {
    govBrControl = <GovBrSelect label={label} {...control.props} />;
  }

  return (
    <div className={`admin-field ${className}`}>
      {govBrControl}
    </div>
  );
}

const ADMIN_EDITOR_TABS = [
  { id: "geral", label: "Dados gerais" },
  { id: "conteudo", label: "Conteúdo" },
  { id: "contato", label: "Contato" },
  { id: "historico", label: "Histórico" }
];

function AdminEditorTabs({ activeTab, auditCount, onChange, revisionCount, sectionCount }) {
  const tabRefs = useRef({});
  const counts = {
    conteudo: sectionCount,
    historico: revisionCount + auditCount
  };

  const handleKeyDown = (event, currentIndex) => {
    let nextIndex = currentIndex;
    if (event.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % ADMIN_EDITOR_TABS.length;
    } else if (event.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + ADMIN_EDITOR_TABS.length) % ADMIN_EDITOR_TABS.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = ADMIN_EDITOR_TABS.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const nextTab = ADMIN_EDITOR_TABS[nextIndex].id;
    onChange(nextTab);
    tabRefs.current[nextTab]?.focus();
  };

  return (
    <div className="admin-editor-tabs" role="tablist" aria-label="Áreas do cadastro do serviço">
      {ADMIN_EDITOR_TABS.map((tab, index) => (
        <button
          aria-controls={`admin-tab-panel-${tab.id}`}
          aria-selected={activeTab === tab.id}
          className={`admin-editor-tab${activeTab === tab.id ? " is-active" : ""}`}
          id={`admin-tab-${tab.id}`}
          key={tab.id}
          onClick={() => onChange(tab.id)}
          onKeyDown={(event) => handleKeyDown(event, index)}
          ref={(element) => { tabRefs.current[tab.id] = element; }}
          role="tab"
          tabIndex={activeTab === tab.id ? 0 : -1}
          type="button"
        >
          <span>{tab.label}</span>
          {counts[tab.id] != null && <span className="admin-editor-tab__count">{counts[tab.id]}</span>}
        </button>
      ))}
    </div>
  );
}

function AdminLinksEditor({
  section,
  sectionIndex,
  onAddLink,
  onUpdateLink,
  onRemoveLink,
  onMoveLink,
  associated = false
}) {
  const itemLabel = structuredItemLabel(section.tipo);
  return (
    <div className="admin-links-editor field--full">
      <div className="admin-links-editor__heading">
        <div>
          <h4>{associated ? "Links associados a esta seção" : section.tipo === "ANEXOS" ? "Anexos desta seção" : "Links desta seção"}</h4>
          <p>
            {associated
              ? "Use estes links para botões, relações entre serviços e destinos usados pelo componente público."
              : section.tipo === "ANEXOS"
                ? "Cadastre documentos já publicados em uma URL acessível. O portal não faz upload físico nesta fase."
                : "Cadastre links estruturados para evitar texto livre com formato especial."}
          </p>
        </div>
        <GovBrButton onClick={() => onAddLink(sectionIndex)}>
          Adicionar {itemLabel}
        </GovBrButton>
      </div>

      {(section.links || []).map((link, linkIndex) => (
        <article className="admin-link-card" key={`secao-${sectionIndex}-link-${linkIndex}`}>
          <div className="admin-link-card__heading">
            <strong>{section.tipo === "ANEXOS" ? "Anexo" : "Link"} {linkIndex + 1}</strong>
            <div className="admin-link-card__actions">
              <GovBrButton disabled={linkIndex === 0} onClick={() => onMoveLink(sectionIndex, linkIndex, -1)}>Subir</GovBrButton>
              <GovBrButton disabled={linkIndex === (section.links || []).length - 1} onClick={() => onMoveLink(sectionIndex, linkIndex, 1)}>Descer</GovBrButton>
              <GovBrButton onClick={() => onRemoveLink(sectionIndex, linkIndex)}>Remover</GovBrButton>
            </div>
          </div>

          <div className="admin-link-card__grid">
            <AdminField label="Tipo do link">
              <select
                value={link.tipo || "LINK_EXTERNO"}
                onChange={(e) => onUpdateLink(sectionIndex, linkIndex, "tipo", e.target.value)}
              >
                {Object.entries(actionTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </AdminField>
            <AdminField label={section.tipo === "ANEXOS" ? "Nome do anexo" : "Rótulo do link"}>
              <input value={link.rotulo} onChange={(e) => onUpdateLink(sectionIndex, linkIndex, "rotulo", e.target.value)} />
            </AdminField>
            <AdminField label={link.tipo === "LINK_INTERNO" ? "Slug do serviço destino" : section.tipo === "ANEXOS" ? "URL do arquivo/documento" : "URL"}>
              <input value={link.url} onChange={(e) => onUpdateLink(sectionIndex, linkIndex, "url", e.target.value)} />
            </AdminField>
            <AdminField label="Componente interno">
              <input value={link.componente || ""} onChange={(e) => onUpdateLink(sectionIndex, linkIndex, "componente", e.target.value)} />
            </AdminField>
            <AdminField label="Descrição/chave" className="field--full">
              <textarea rows="2" value={link.descricao} onChange={(e) => onUpdateLink(sectionIndex, linkIndex, "descricao", e.target.value)} />
            </AdminField>
            <AdminField label="Abrir em nova aba">
              <select value={link.abrirNovaAba ? "true" : "false"} onChange={(e) => onUpdateLink(sectionIndex, linkIndex, "abrirNovaAba", e.target.value === "true")}>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </AdminField>
          </div>
        </article>
      ))}
    </div>
  );
}

function AdminDashboard({ permissions, onBack, onManageAccess }) {
  const adminTitleRef = useRef(null);
  const [services, setServices] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeAdminTab, setActiveAdminTab] = useState("geral");
  const [homeConfig, setHomeConfig] = useState({ textoAviso: HOME_ANNOUNCEMENT_TEXT });
  const [homeSaving, setHomeSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateHomeConfig = (field, value) => setHomeConfig((current) => ({ ...current, [field]: value }));
  const updateSection = (sectionIndex, field, value) => {
    setForm((current) => ({
      ...current,
      secoes: (current.secoes || []).map((section, index) => (
        index === sectionIndex ? { ...section, [field]: value } : section
      ))
    }));
  };
  const changeSectionType = (sectionIndex, type) => {
    setForm((current) => ({
      ...current,
      secoes: (current.secoes || []).map((section, index) => (
        index === sectionIndex
          ? {
              ...section,
              tipo: type,
              titulo: section.titulo || sectionTypeLabels[type] || "Seção",
              texto: type === "ETAPAS" || usesStructuredItems(type) ? "" : section.texto,
              etapas: type === "ETAPAS" ? (section.etapas?.length > 0 ? section.etapas : [createEmptyStep()]) : [],
              links: section.links?.length > 0 ? section.links : (usesStructuredItems(type) ? [createEmptyLink()] : [])
            }
          : section
      ))
    }));
  };
  const addSection = () => {
    setForm((current) => ({
      ...current,
      secoes: [...(current.secoes || []), createSection()]
    }));
  };
  const removeSection = (sectionIndex) => {
    setForm((current) => {
      const secoes = (current.secoes || []).filter((_, index) => index !== sectionIndex);
      return {
        ...current,
        secoes: secoes.length > 0 ? secoes : [createSection()]
      };
    });
  };
  const moveSection = (sectionIndex, direction) => {
    setForm((current) => {
      const secoes = [...(current.secoes || [])];
      const targetIndex = sectionIndex + direction;
      if (targetIndex < 0 || targetIndex >= secoes.length) {
        return current;
      }
      [secoes[sectionIndex], secoes[targetIndex]] = [secoes[targetIndex], secoes[sectionIndex]];
      return { ...current, secoes };
    });
  };
  const updateLink = (sectionIndex, linkIndex, field, value) => {
    setForm((current) => ({
      ...current,
      secoes: (current.secoes || []).map((section, index) => (
        index === sectionIndex
          ? {
              ...section,
              links: (section.links || []).map((link, currentLinkIndex) => (
                currentLinkIndex === linkIndex ? { ...link, [field]: value } : link
              ))
            }
          : section
      ))
    }));
  };
  const addLink = (sectionIndex) => {
    setForm((current) => ({
      ...current,
      secoes: (current.secoes || []).map((section, index) => (
        index === sectionIndex
          ? { ...section, links: [...(section.links || []), createEmptyLink()] }
          : section
      ))
    }));
  };
  const removeLink = (sectionIndex, linkIndex) => {
    setForm((current) => ({
      ...current,
      secoes: (current.secoes || []).map((section, index) => {
        if (index !== sectionIndex) {
          return section;
        }
        const links = (section.links || []).filter((_, currentLinkIndex) => currentLinkIndex !== linkIndex);
        return { ...section, links: links.length > 0 ? links : [createEmptyLink()] };
      })
    }));
  };
  const moveLink = (sectionIndex, linkIndex, direction) => {
    setForm((current) => {
      const secoes = (current.secoes || []).map((section, index) => {
        if (index !== sectionIndex) {
          return section;
        }
        const links = [...(section.links || [])];
        const targetIndex = linkIndex + direction;
        if (targetIndex < 0 || targetIndex >= links.length) {
          return section;
        }
        [links[linkIndex], links[targetIndex]] = [links[targetIndex], links[linkIndex]];
        return { ...section, links };
      });
      return { ...current, secoes };
    });
  };
  const updateStep = (sectionIndex, stepIndex, field, value) => {
    setForm((current) => ({
      ...current,
      secoes: (current.secoes || []).map((section, index) => (
        index === sectionIndex
          ? {
              ...section,
              etapas: (section.etapas || []).map((step, currentStepIndex) => (
                currentStepIndex === stepIndex ? { ...step, [field]: value } : step
              ))
            }
          : section
      ))
    }));
  };
  const addStep = (sectionIndex) => {
    setForm((current) => ({
      ...current,
      secoes: (current.secoes || []).map((section, index) => (
        index === sectionIndex
          ? { ...section, etapas: [...(section.etapas || []), createEmptyStep()] }
          : section
      ))
    }));
  };
  const removeStep = (sectionIndex, stepIndex) => {
    setForm((current) => ({
      ...current,
      secoes: (current.secoes || []).map((section, index) => {
        if (index !== sectionIndex) {
          return section;
        }
        const etapas = (section.etapas || []).filter((_, currentStepIndex) => currentStepIndex !== stepIndex);
        return { ...section, etapas: etapas.length > 0 ? etapas : [createEmptyStep()] };
      })
    }));
  };
  const moveStep = (sectionIndex, stepIndex, direction) => {
    setForm((current) => {
      const secoes = (current.secoes || []).map((section, index) => {
        if (index !== sectionIndex) {
          return section;
        }
        const etapas = [...(section.etapas || [])];
        const targetIndex = stepIndex + direction;
        if (targetIndex < 0 || targetIndex >= etapas.length) {
          return section;
        }
        [etapas[stepIndex], etapas[targetIndex]] = [etapas[targetIndex], etapas[stepIndex]];
        return { ...section, etapas };
      });
      return { ...current, secoes };
    });
  };
  const previewService = useMemo(() => formToPreviewService(form), [form]);
  const currentPermissions = useMemo(() => new Set(form.permissoes || []), [form.permissoes]);
  const canEditCurrent = form.id
    ? currentPermissions.has(PERMISSAO_EDITAR)
    : permissions.canCreate;
  const canPublishCurrent = Boolean(form.id) && currentPermissions.has(PERMISSAO_PUBLICAR);
  const canUnpublishCurrent = Boolean(form.id) && currentPermissions.has(PERMISSAO_DESPUBLICAR);
  const canArchiveCurrent = Boolean(form.id) && currentPermissions.has(PERMISSAO_ARQUIVAR);
  const canRestoreCurrent = Boolean(form.id)
    && currentPermissions.has(PERMISSAO_RESTAURAR_REVISAO)
    && currentPermissions.has(PERMISSAO_EDITAR);

  const loadList = async () => {
    setLoading(true);
    setError(null);
    try {
      setServices(await listarServicosAdmin());
    } catch (exception) {
      setError(exception?.message || "Erro ao carregar administração.");
    } finally {
      setLoading(false);
    }
  };

  const loadHomeConfig = async () => {
    try {
      const data = await buscarHomeConfigAdmin();
      setHomeConfig({ textoAviso: data?.textoAviso || HOME_ANNOUNCEMENT_TEXT });
    } catch (exception) {
      setError(exception?.message || "Erro ao carregar configurações da página inicial.");
    }
  };

  const loadRevisions = async (id) => {
    if (!id) {
      setRevisions([]);
      return;
    }
    try {
      setRevisions(await listarRevisoesServicoAdmin(id));
    } catch (exception) {
      setError(exception?.message || "Erro ao carregar histórico.");
    }
  };

  const loadAudit = async (id) => {
    if (!id) {
      setAuditEvents([]);
      return;
    }
    try {
      setAuditEvents(await listarAuditoriaServicoAdmin(id));
    } catch (exception) {
      setError(exception?.message || "Erro ao carregar auditoria.");
    }
  };

  const selectService = async (id) => {
    setSelectedId(id);
    setActiveAdminTab("geral");
    setMessage(null);
    setError(null);
    try {
      const data = await buscarServicoAdmin(id);
      setForm(adminToForm(data));
      await loadRevisions(id);
      await loadAudit(id);
    } catch (exception) {
      setError(exception?.message || "Erro ao carregar serviço.");
    }
  };

  const newService = () => {
    setSelectedId(null);
    setActiveAdminTab("geral");
    setForm(createEmptyForm());
    setRevisions([]);
    setAuditEvents([]);
    setPreviewOpen(false);
    setMessage(null);
    setError(null);
  };

  const save = async () => {
    const validationErrors = validateForm(form);
    if (validationErrors.length > 0) {
      setActiveAdminTab(adminTabForValidationErrors(validationErrors));
      setMessage(null);
      setError(formatValidationErrors(validationErrors));
      scrollPortalToTop("admin");
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const payload = formToPayload(form);
      const saved = form.id ? await atualizarServicoAdmin(form.id, payload) : await criarServicoAdmin(payload);
      setForm(adminToForm(saved));
      setSelectedId(saved.id);
      setMessage("Serviço salvo como rascunho/alteração administrativa.");
      await loadList();
      await loadRevisions(saved.id);
      await loadAudit(saved.id);
    } catch (exception) {
      setError(exception?.response?.data?.message || exception?.message || "Erro ao salvar serviço.");
    } finally {
      setSaving(false);
    }
  };

  const saveHomeConfig = async () => {
    if (!hasText(homeConfig.textoAviso)) {
      setMessage(null);
      setError("Informe o texto do aviso da página inicial.");
      return;
    }
    setHomeSaving(true);
    setMessage(null);
    setError(null);
    try {
      const saved = await atualizarHomeConfigAdmin({ textoAviso: homeConfig.textoAviso });
      setHomeConfig({ textoAviso: saved?.textoAviso || HOME_ANNOUNCEMENT_TEXT });
      setMessage("Texto da página inicial salvo.");
    } catch (exception) {
      setError(exception?.response?.data?.message || exception?.message || "Erro ao salvar texto da página inicial.");
    } finally {
      setHomeSaving(false);
    }
  };

  const publish = async () => {
    if (!form.id) {
      setMessage(null);
      setError("Salve o serviço antes de publicar.");
      return;
    }
    if (!window.confirm("Publicar a última versão salva? Alterações ainda não salvas no formulário não serão incluídas.")) {
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const updated = await publicarServicoAdmin(form.id);
      setForm(adminToForm(updated));
      setMessage("Última versão salva publicada.");
      await loadList();
      await loadRevisions(updated.id);
      await loadAudit(updated.id);
    } catch (exception) {
      setError(exception?.response?.data?.message || exception?.message || "Erro ao publicar serviço.");
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (action, successMessage, confirmation) => {
    if (!form.id) {
      return;
    }
    if (confirmation && !window.confirm(confirmation)) {
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const updated = await action(form.id);
      setForm(adminToForm(updated));
      setMessage(successMessage);
      await loadList();
      await loadRevisions(updated.id);
      await loadAudit(updated.id);
    } catch (exception) {
      setError(exception?.response?.data?.message || exception?.message || "Erro ao executar ação.");
    } finally {
      setSaving(false);
    }
  };

  const restoreRevision = async (revisionId) => {
    if (!form.id) {
      return;
    }
    if (!window.confirm("Restaurar esta revisão como nova versão de trabalho? A versão pública atual será preservada até uma nova publicação.")) {
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const updated = await restaurarRevisaoServicoAdmin(form.id, revisionId);
      setForm(adminToForm(updated));
      setMessage("Revisão restaurada na versão de trabalho. A versão pública não foi alterada.");
      await loadList();
      await loadRevisions(updated.id);
      await loadAudit(updated.id);
    } catch (exception) {
      setError(exception?.response?.data?.message || exception?.message || "Erro ao restaurar revisão.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadList();
    if (permissions.canAdmin) {
      loadHomeConfig();
    }
    scrollPortalToTop("admin");
    focusAfterRender(adminTitleRef);
  }, [permissions.canAdmin]);

  useIframeHeightBridge();

  useEffect(() => {
    postHeightToParent();
  }, [activeAdminTab, form, homeConfig, revisions, auditEvents, loading, previewOpen, error, message]);

  return (
    <main className="portal-services-shell portal-admin-shell" id="portal-servicos-admin">
      <div className="admin-topbar">
        <div>
          <span className="service-category">Administração</span>
          <h1 ref={adminTitleRef} tabIndex="-1">Cadastro de Serviços do Portal</h1>
          <p>Edite os textos estruturados que aparecem na lista pública e na página padrão de cada serviço.</p>
        </div>
          <div className="admin-actions">
            {permissions.canManageAccess && <GovBrButton onClick={onManageAccess}>Gerenciar acessos</GovBrButton>}
            <GovBrButton onClick={onBack}>Voltar ao portal público</GovBrButton>
          </div>
      </div>

      {error && (
        <div className="admin-message">
          <br-message
            message={error}
            show-icon="true"
            state="danger"
          ></br-message>
        </div>
      )}
      {message && (
        <div className="admin-message">
          <br-message
            message={message}
            show-icon="true"
            state="success"
          ></br-message>
        </div>
      )}

      {permissions.canAdmin && <section className="admin-home-config-panel">
        <div className="admin-form-heading">
          <div>
            <h2>Configurações da página inicial</h2>
            <p>Texto exibido no aviso azul acima dos cards de serviços.</p>
          </div>
          <GovBrButton emphasis="primary" disabled={homeSaving} loading={homeSaving} onClick={saveHomeConfig}>
            Salvar texto
          </GovBrButton>
        </div>
        <div className="admin-form-grid">
          <AdminField label="Texto do aviso" className="field--full">
            <textarea
              rows="3"
              value={homeConfig.textoAviso}
              onChange={(event) => updateHomeConfig("textoAviso", event.target.value)}
            />
          </AdminField>
        </div>
      </section>}

      <div className="admin-layout">
        <aside className="admin-list-panel">
          <div className="admin-list-header">
            <h2>Serviços</h2>
            {permissions.canCreate && <GovBrButton onClick={newService}>Novo</GovBrButton>}
          </div>
          {loading ? <LoadingState /> : (
            <div className="admin-service-list">
              {services.map((service) => (
                <button
                  className={`admin-service-row${selectedId === service.id ? " is-selected" : ""}`}
                  type="button"
                  key={service.id}
                  onClick={() => selectService(service.id)}
                >
                  <strong>{service.titulo}</strong>
                  <span>{adminStatusLabel(service)} · {service.status === "PUBLICADO" || service.alteracoesPendentes ? "versão pública ativa" : "fora do portal público"}</span>
                </button>
              ))}
            </div>
          )}
        </aside>

        <section className="admin-form-panel">
          <div className="admin-editor-toolbar">
            <div className="admin-form-heading">
              <div>
                <h2>{form.id ? "Editar serviço" : "Novo serviço"}</h2>
                <p>Status atual: <strong>{adminStatusLabel(form)}</strong></p>
              </div>
              <div className="admin-actions">
                <GovBrButton onClick={() => setPreviewOpen((current) => !current)}>
                  {previewOpen ? "Fechar preview" : "Preview"}
                </GovBrButton>
                {canEditCurrent && <GovBrButton emphasis="primary" disabled={saving} loading={saving} onClick={save}>Salvar versão de trabalho</GovBrButton>}
                {canPublishCurrent && <GovBrButton disabled={saving} onClick={publish}>Publicar versão salva</GovBrButton>}
                {canUnpublishCurrent && (form.status === "PUBLICADO" || form.alteracoesPendentes) && <GovBrButton disabled={saving} onClick={() => runAction(despublicarServicoAdmin, "Serviço retirado da publicação e mantido como rascunho.", "Retirar este serviço da publicação? O conteúdo e o histórico serão preservados.")}>Retirar da publicação</GovBrButton>}
                {canArchiveCurrent && <GovBrButton disabled={saving} onClick={() => runAction(arquivarServicoAdmin, "Serviço arquivado sem exclusão de dados.", "Arquivar este serviço? Ele sairá do portal público, mas nenhum dado será excluído.")}>Arquivar</GovBrButton>}
              </div>
            </div>
            <AdminEditorTabs
              activeTab={activeAdminTab}
              auditCount={auditEvents.length}
              onChange={setActiveAdminTab}
              revisionCount={revisions.length}
              sectionCount={(form.secoes || []).length}
            />
          </div>

          {previewOpen && (
            <section className="admin-preview">
              <div className="admin-preview__heading">
                <span className="service-category">Preview administrativo</span>
                <p>Prévia gerada a partir do formulário atual. Salvar ou publicar ainda depende dos botões administrativos.</p>
              </div>
              <ServiceDetail
                service={previewService}
                preview
                backLabel="Fechar preview"
                onBack={() => setPreviewOpen(false)}
              />
            </section>
          )}

          {!canEditCurrent && form.id && (
            <div className="admin-readonly-message">
              <br-message message="Você pode consultar e executar somente as ações autorizadas para este serviço; a edição do formulário está bloqueada." show-icon="true" state="info"></br-message>
            </div>
          )}

          <fieldset className="admin-edit-fields" disabled={!canEditCurrent}>
          <section
            aria-labelledby="admin-tab-geral"
            className="admin-tab-panel"
            hidden={activeAdminTab !== "geral"}
            id="admin-tab-panel-geral"
            role="tabpanel"
          >
            <div className="admin-form-grid">
              <AdminField label="Título" className="field--full"><input value={form.titulo} onChange={(e) => updateForm("titulo", e.target.value)} /></AdminField>
            <AdminField label="Slug"><input value={form.slug} onChange={(e) => updateForm("slug", e.target.value)} placeholder="gerado a partir do título se vazio" /></AdminField>
            <AdminField label="Categoria"><input value={form.categoria} onChange={(e) => updateForm("categoria", e.target.value)} /></AdminField>
            <AdminField label="Resumo do card" className="field--full"><textarea rows="3" value={form.resumo} onChange={(e) => updateForm("resumo", e.target.value)} /></AdminField>
            <AdminField label="Conhecido como" className="field--full"><textarea rows="3" value={form.conhecidoComo} onChange={(e) => updateForm("conhecidoComo", e.target.value)} /></AdminField>
            <AdminField label="Ícone"><input value={form.icone} onChange={(e) => updateForm("icone", e.target.value)} /></AdminField>
            <AdminField label="Ordem"><input type="number" value={form.ordem} onChange={(e) => updateForm("ordem", e.target.value)} /></AdminField>
            <AdminField label="Tipo da ação">
              <select value={form.acaoTipo} onChange={(e) => updateForm("acaoTipo", e.target.value)}>
                {Object.entries(actionTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </AdminField>
            <AdminField label="Rótulo da ação"><input value={form.acaoRotulo} onChange={(e) => updateForm("acaoRotulo", e.target.value)} /></AdminField>
            <AdminField label={form.acaoTipo === "LINK_INTERNO" ? "Slug do serviço destino" : "URL da ação"}><input value={form.acaoUrl} onChange={(e) => updateForm("acaoUrl", e.target.value)} /></AdminField>
            <AdminField label="Componente interno"><input value={form.acaoComponente} onChange={(e) => updateForm("acaoComponente", e.target.value)} /></AdminField>
              <AdminField label="Abrir ação em nova aba">
                <select value={form.abrirNovaAba ? "true" : "false"} onChange={(e) => updateForm("abrirNovaAba", e.target.value === "true")}>
                  <option value="true">Sim</option>
                  <option value="false">Não</option>
                </select>
              </AdminField>
            </div>
          </section>

          <section
            aria-labelledby="admin-tab-conteudo"
            className="admin-tab-panel"
            hidden={activeAdminTab !== "conteudo"}
            id="admin-tab-panel-conteudo"
            role="tabpanel"
          >
            <div className="admin-form-grid admin-form-grid--content">
              <section className="admin-sections-editor field--full">
              <div className="admin-sections-editor__heading">
                <div>
                  <h3>Seções da página do serviço</h3>
                  <p>Monte os blocos exibidos na página pública. A ordem abaixo será preservada na publicação.</p>
                </div>
                <GovBrButton onClick={addSection}>Adicionar seção</GovBrButton>
              </div>

              {(form.secoes || []).map((section, sectionIndex) => (
                <article className="admin-section-card" key={`${section.id || section.titulo}-${sectionIndex}`}>
                  <div className="admin-section-card__heading">
                    <strong>Seção {sectionIndex + 1}</strong>
                    <div className="admin-section-card__actions">
                      <GovBrButton disabled={sectionIndex === 0} onClick={() => moveSection(sectionIndex, -1)}>Subir</GovBrButton>
                      <GovBrButton disabled={sectionIndex === (form.secoes || []).length - 1} onClick={() => moveSection(sectionIndex, 1)}>Descer</GovBrButton>
                      <GovBrButton onClick={() => removeSection(sectionIndex)}>Remover</GovBrButton>
                    </div>
                  </div>

                  <div className="admin-section-card__grid">
                    <AdminField label="Tipo da seção">
                      <select value={section.tipo} onChange={(e) => changeSectionType(sectionIndex, e.target.value)}>
                        <option value="TEXTO">Texto</option>
                        <option value="ETAPAS">Etapas</option>
                        <option value="ALERTA">Alerta</option>
                        <option value="LINKS">Links</option>
                        <option value="ANEXOS">Anexos</option>
                        <option value="CONTATO">Contato</option>
                      </select>
                    </AdminField>
                    <AdminField label="ID técnico opcional">
                      <input value={section.id} onChange={(e) => updateSection(sectionIndex, "id", e.target.value)} placeholder="gerado pelo título se vazio" />
                    </AdminField>
                    <AdminField label="Título da seção" className="field--full">
                      <input value={section.titulo} onChange={(e) => updateSection(sectionIndex, "titulo", e.target.value)} />
                    </AdminField>

                    {section.tipo === "ETAPAS" ? (
                      <div className="admin-steps-editor field--full">
                        <div className="admin-steps-editor__heading">
                          <div>
                            <h4>Etapas desta seção</h4>
                            <p>Cadastre quantas etapas forem necessárias para este bloco.</p>
                          </div>
                          <GovBrButton onClick={() => addStep(sectionIndex)}>Adicionar etapa</GovBrButton>
                        </div>

                        {(section.etapas || []).map((etapa, stepIndex) => (
                          <article className="admin-step-card" key={`secao-${sectionIndex}-etapa-${stepIndex}`}>
                            <div className="admin-step-card__heading">
                              <strong>Etapa {stepIndex + 1}</strong>
                              <div className="admin-step-card__actions">
                                <GovBrButton disabled={stepIndex === 0} onClick={() => moveStep(sectionIndex, stepIndex, -1)}>Subir</GovBrButton>
                                <GovBrButton disabled={stepIndex === (section.etapas || []).length - 1} onClick={() => moveStep(sectionIndex, stepIndex, 1)}>Descer</GovBrButton>
                                <GovBrButton onClick={() => removeStep(sectionIndex, stepIndex)}>Remover</GovBrButton>
                              </div>
                            </div>

                            <div className="admin-step-card__grid">
                              <AdminField label="Nome da etapa" className="field--full"><input value={etapa.nome} onChange={(e) => updateStep(sectionIndex, stepIndex, "nome", e.target.value)} /></AdminField>
                              <AdminField label="Descrição da etapa" className="field--full"><textarea rows="3" value={etapa.descricao} onChange={(e) => updateStep(sectionIndex, stepIndex, "descricao", e.target.value)} /></AdminField>
                              <AdminField label="Canal"><input value={etapa.canal} onChange={(e) => updateStep(sectionIndex, stepIndex, "canal", e.target.value)} /></AdminField>
                              <AdminField label="Duração"><input value={etapa.duracao} onChange={(e) => updateStep(sectionIndex, stepIndex, "duracao", e.target.value)} /></AdminField>
                              <AdminField label="Custos"><input value={etapa.custos} onChange={(e) => updateStep(sectionIndex, stepIndex, "custos", e.target.value)} /></AdminField>
                              <AdminField label="Documentos, um por linha"><textarea rows="3" value={etapa.documentos} onChange={(e) => updateStep(sectionIndex, stepIndex, "documentos", e.target.value)} /></AdminField>
                              <AdminField label="Antes de começar" className="field--full"><textarea rows="3" value={etapa.requisitos} onChange={(e) => updateStep(sectionIndex, stepIndex, "requisitos", e.target.value)} /></AdminField>
                              <AdminField label="Acompanhamento" className="field--full"><textarea rows="3" value={etapa.acompanhamento} onChange={(e) => updateStep(sectionIndex, stepIndex, "acompanhamento", e.target.value)} /></AdminField>
                            </div>
                          </article>
                        ))}
                        <AdminLinksEditor
                          section={section}
                          sectionIndex={sectionIndex}
                          onAddLink={addLink}
                          onUpdateLink={updateLink}
                          onRemoveLink={removeLink}
                          onMoveLink={moveLink}
                          associated
                        />
                      </div>
                    ) : usesStructuredItems(section.tipo) ? (
                      <AdminLinksEditor
                        section={section}
                        sectionIndex={sectionIndex}
                        onAddLink={addLink}
                        onUpdateLink={updateLink}
                        onRemoveLink={removeLink}
                        onMoveLink={moveLink}
                      />
                    ) : (
                      <>
                        <AdminField
                          label="Texto da seção"
                          className="field--full"
                        >
                          <textarea rows="5" value={section.texto} onChange={(e) => updateSection(sectionIndex, "texto", e.target.value)} />
                        </AdminField>
                        <AdminLinksEditor
                          section={section}
                          sectionIndex={sectionIndex}
                          onAddLink={addLink}
                          onUpdateLink={updateLink}
                          onRemoveLink={removeLink}
                          onMoveLink={moveLink}
                          associated
                        />
                      </>
                    )}
                  </div>
                </article>
                ))}
              </section>
            </div>
          </section>

          <section
            aria-labelledby="admin-tab-contato"
            className="admin-tab-panel"
            hidden={activeAdminTab !== "contato"}
            id="admin-tab-panel-contato"
            role="tabpanel"
          >
            <div className="admin-form-grid">
              <AdminField label="Unidade de ajuda"><input value={form.contatoUnidade} onChange={(e) => updateForm("contatoUnidade", e.target.value)} /></AdminField>
              <AdminField label="Suporte técnico"><input value={form.contatoSuporteTecnico} onChange={(e) => updateForm("contatoSuporteTecnico", e.target.value)} /></AdminField>
              <AdminField label="Suporte negocial"><input value={form.contatoSuporteNegocial} onChange={(e) => updateForm("contatoSuporteNegocial", e.target.value)} /></AdminField>
              <AdminField label="E-mail"><input value={form.contatoEmail} onChange={(e) => updateForm("contatoEmail", e.target.value)} /></AdminField>
              <AdminField label="Endereço"><input value={form.contatoEndereco} onChange={(e) => updateForm("contatoEndereco", e.target.value)} /></AdminField>
              <AdminField label="Horário"><input value={form.contatoHorario} onChange={(e) => updateForm("contatoHorario", e.target.value)} /></AdminField>
              <AdminField label="Tags, uma por linha" className="field--full"><textarea rows="4" value={form.tags} onChange={(e) => updateForm("tags", e.target.value)} /></AdminField>
            </div>
          </section>
          </fieldset>

          <section
            aria-labelledby="admin-tab-historico"
            className="admin-tab-panel admin-tab-panel--history"
            hidden={activeAdminTab !== "historico"}
            id="admin-tab-panel-historico"
            role="tabpanel"
          >
            {form.id ? (
              <>
              <section className="admin-revisions">
                <div className="admin-revisions__heading">
                  <div>
                    <h3>Histórico de revisões</h3>
                    <p>A restauração cria uma nova versão de trabalho e preserva a versão pública atual.</p>
                  </div>
                  <GovBrButton onClick={() => loadRevisions(form.id)}>
                    Atualizar histórico
                  </GovBrButton>
                </div>

                {revisions.length === 0 ? (
                  <div className="empty-inline">Nenhuma revisão registrada para este serviço.</div>
                ) : (
                  <div className="revision-list">
                    {revisions.map((revision) => (
                      <article className="revision-row" key={revision.id}>
                        <div>
                          <strong>Versão {revision.versao} · {revision.acao}</strong>
                          <span>{revision.tituloSnapshot || "Sem título"} · {revision.statusSnapshot || "-"}</span>
                          <small>{revision.criadoPor || "sistema"} · {revision.criadoEm || "-"}</small>
                        </div>
                        {canRestoreCurrent && (
                          <GovBrButton disabled={saving} onClick={() => restoreRevision(revision.id)}>
                            Restaurar
                          </GovBrButton>
                        )}
                      </article>
                    ))}
                  </div>
                )}
              </section>

              <section className="admin-revisions">
                <div className="admin-revisions__heading">
                  <div>
                    <h3>Auditoria operacional</h3>
                    <p>Eventos administrativos registrados para rastrear alterações e publicações.</p>
                  </div>
                  <GovBrButton onClick={() => loadAudit(form.id)}>
                    Atualizar auditoria
                  </GovBrButton>
                </div>

                {auditEvents.length === 0 ? (
                  <div className="empty-inline">Nenhum evento de auditoria registrado para este serviço.</div>
                ) : (
                  <div className="revision-list">
                    {auditEvents.map((event) => (
                      <article className="revision-row revision-row--audit" key={event.id}>
                        <div>
                          <strong>{event.acao} · {event.status || "-"}</strong>
                          <span>{event.detalhe || "Evento administrativo registrado."}</span>
                          <small>{event.usuario || "sistema"} · {event.dataHora || "-"}</small>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
              </>
            ) : (
              <div className="admin-tab-empty">
                <strong>O histórico estará disponível depois do primeiro salvamento.</strong>
                <span>Salve o novo serviço para acompanhar revisões, publicações e eventos de auditoria.</span>
              </div>
            )}
          </section>
        </section>
      </div>
    </main>
  );
}

function measureDocumentHeight() {
  return Math.max(
    document.documentElement.scrollHeight,
    document.documentElement.offsetHeight,
    document.body?.scrollHeight || 0,
    document.body?.offsetHeight || 0,
    MIN_IFRAME_HEIGHT
  );
}

function postHeightToParent() {
  if (window.parent === window) {
    return;
  }
  if (iframeHeightFrame) {
    window.cancelAnimationFrame(iframeHeightFrame);
  }
  iframeHeightFrame = window.requestAnimationFrame(() => {
    iframeHeightFrame = null;
    window.parent.postMessage(
      {
        type: IFRAME_HEIGHT_MESSAGE_TYPE,
        app: IFRAME_APP_ID,
        height: measureDocumentHeight(),
        href: window.location.href
      },
      "*"
    );
  });
}

function postNavigationToParent(target) {
  if (window.parent === window) {
    return;
  }
  window.parent.postMessage(
    {
      type: IFRAME_NAVIGATION_MESSAGE_TYPE,
      app: IFRAME_APP_ID,
      target,
      href: window.location.href
    },
    "*"
  );
}

function scrollPortalToTop(target) {
  if (window.parent === window) {
    window.scrollTo({ top: 0, behavior: "smooth" });
  } else {
    postNavigationToParent(target);
  }
  postHeightToParent();
}

function focusAfterRender(ref) {
  window.requestAnimationFrame(() => {
    ref.current?.focus({ preventScroll: true });
  });
}

function useIframeHeightBridge() {
  useEffect(() => {
    if (window.parent === window) {
      return undefined;
    }

    const sendHeight = () => postHeightToParent();
    const resizeObserver = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(sendHeight);

    resizeObserver?.observe(document.documentElement);
    if (document.body) {
      resizeObserver?.observe(document.body);
    }

    window.addEventListener("load", sendHeight);
    window.addEventListener("resize", sendHeight);
    sendHeight();

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("load", sendHeight);
      window.removeEventListener("resize", sendHeight);
      if (iframeHeightFrame) {
        window.cancelAnimationFrame(iframeHeightFrame);
        iframeHeightFrame = null;
      }
    };
  }, []);
}

export default function App({ keycloak }) {
  const listTitleRef = useRef(null);
  const detailTitleRef = useRef(null);
  const adminLoginStartedRef = useRef(false);
  const serviceRequestRef = useRef(0);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [activeInternalAction, setActiveInternalAction] = useState(null);
  const [homeConfig, setHomeConfig] = useState({ textoAviso: HOME_ANNOUNCEMENT_TEXT });
  const [searchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adminAuthError, setAdminAuthError] = useState("");
  const [adminContext, setAdminContext] = useState(null);
  const [adminContextLoading, setAdminContextLoading] = useState(false);
  const [adminContextError, setAdminContextError] = useState("");
  const normalizedPath = typeof window === "undefined"
    ? ""
    : window.location.pathname.replace(/\/+$/, "");
  const accessAdminRoute = normalizedPath === ACCESS_ADMIN_PATH
    || normalizedPath.startsWith(`${ACCESS_ADMIN_PATH}/`);
  const adminRoute = normalizedPath === ADMIN_PATH
    || normalizedPath.startsWith(`${ADMIN_PATH}/`);
  const trackingServiceSlug = selectedService?.slug || "";
  const trackingServiceTitle = selectedService?.titulo || "";
  const trackingInternalActionActive = Boolean(activeInternalAction);

  const permissions = useMemo(() => {
    const canAdmin = adminContext?.superAdministrador === true;
    const canManageAccess = adminContext?.gestorAcessos === true;
    const canCreate = adminContext?.podeCriarServico === true;
    const canViewAdmin = canAdmin || canCreate || (adminContext?.servicos || []).length > 0;
    return { canViewAdmin, canCreate, canAdmin, canManageAccess };
  }, [adminContext]);

  const loadServices = async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, config] = await Promise.all([
        listarServicosPublicos(),
        buscarHomeConfigPublico().catch(() => ({ textoAviso: HOME_ANNOUNCEMENT_TEXT }))
      ]);
      setServices(data);
      setHomeConfig({ textoAviso: config?.textoAviso || HOME_ANNOUNCEMENT_TEXT });
    } catch (exception) {
      setError(exception?.message || "Erro inesperado ao consultar a API.");
    } finally {
      setLoading(false);
    }
  };

  const selectService = useCallback(async (slug, { updateHistory = true } = {}) => {
    const requestId = ++serviceRequestRef.current;
    setDetailLoading(true);
    setSelectedService(null);
    setActiveInternalAction(null);
    setError(null);
    try {
      const data = await buscarServicoPublico(slug);
      if (requestId !== serviceRequestRef.current) {
        return;
      }
      const servicePath = getServicePath(slug);
      if (updateHistory && window.location.pathname !== servicePath) {
        window.history.pushState(null, "", servicePath);
      }
      setSelectedService(data);
      scrollPortalToTop("detalhe");
    } catch (exception) {
      if (requestId === serviceRequestRef.current) {
        setError(exception?.message || "Erro inesperado ao consultar o serviço.");
      }
    } finally {
      if (requestId === serviceRequestRef.current) {
        setDetailLoading(false);
      }
    }
  }, []);

  const showServicesList = useCallback(() => {
    serviceRequestRef.current += 1;
    setSelectedService(null);
    setActiveInternalAction(null);
    setDetailLoading(false);
    setError(null);
    scrollPortalToTop("lista");
    focusAfterRender(listTitleRef);
  }, []);

  useEffect(() => {
    if (adminRoute) {
      return undefined;
    }

    const restoreServiceFromUrl = () => {
      const slug = getServiceSlug();
      if (slug) {
        selectService(slug, { updateHistory: false });
      } else {
        showServicesList();
      }
    };

    restoreServiceFromUrl();
    window.addEventListener("popstate", restoreServiceFromUrl);
    return () => {
      serviceRequestRef.current += 1;
      window.removeEventListener("popstate", restoreServiceFromUrl);
    };
  }, [adminRoute, selectService, showServicesList]);

  useEffect(() => {
    if (adminRoute) {
      setLoading(false);
      return;
    }
    loadServices();
  }, [adminRoute]);

  useEffect(() => {
    if (adminRoute) {
      return;
    }

    const googleAnalyticsEnabled = initializeGoogleAnalytics();
    const hotjarEnabled = initializeHotjar();

    if (!googleAnalyticsEnabled && !hotjarEnabled) {
      return;
    }

    let pagePath = "/";
    let pageTitle = "Portal de Serviços do TST";

    if (trackingServiceSlug) {
      const servicePath = `/servicos/${encodeURIComponent(trackingServiceSlug)}`;
      pagePath = trackingInternalActionActive
        ? `${servicePath}/acao`
        : servicePath;
      pageTitle = trackingServiceTitle
        ? `${trackingServiceTitle} | Portal de Serviços do TST`
        : "Serviço | Portal de Serviços do TST";
    }

    if (googleAnalyticsEnabled) {
      trackGoogleAnalyticsPageView(pagePath, pageTitle);
    }

    if (hotjarEnabled) {
      trackHotjarPageView(pagePath);
    }
  }, [
    adminRoute,
    trackingInternalActionActive,
    trackingServiceSlug,
    trackingServiceTitle
  ]);

  useEffect(() => {
    if (
      !adminRoute ||
      keycloak?.authenticated ||
      adminLoginStartedRef.current
    ) {
      return;
    }

    if (typeof keycloak?.login !== "function") {
      setAdminAuthError("O serviço de autenticação não está disponível.");
      return;
    }

    adminLoginStartedRef.current = true;
    Promise.resolve(
      keycloak.login({
        redirectUri: window.location.href
      })
    ).catch((exception) => {
      adminLoginStartedRef.current = false;
      setAdminAuthError(
        exception?.message || "Não foi possível iniciar a autenticação."
      );
    });
  }, [adminRoute, keycloak]);

  useEffect(() => {
    if (!adminRoute || !keycloak?.authenticated) {
      return undefined;
    }
    let active = true;
    setAdminContextLoading(true);
    setAdminContextError("");
    buscarContextoAdmin()
      .then((context) => {
        if (active) {
          setAdminContext(context);
        }
      })
      .catch((exception) => {
        if (active) {
          setAdminContextError(
            exception?.response?.data?.message || exception?.message || "Não foi possível consultar as permissões do usuário."
          );
        }
      })
      .finally(() => {
        if (active) {
          setAdminContextLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [adminRoute, keycloak?.authenticated]);

  useIframeHeightBridge();

  useEffect(() => {
    postHeightToParent();
  }, [services, homeConfig, selectedService, activeInternalAction, loading, detailLoading, error, adminRoute, adminAuthError, adminContextLoading, adminContextError]);

  useEffect(() => {
    if (selectedService && !detailLoading) {
      scrollPortalToTop("detalhe");
      focusAfterRender(detailTitleRef);
    }
  }, [selectedService, detailLoading]);

  const filteredServices = useMemo(() => {
    const currentServices = services;
    const query = normalize(searchQuery.trim());
    if (!query) {
      return currentServices;
    }
    return currentServices.filter((service) => {
      const text = normalize(searchableText(service));
      return text.includes(query);
    });
  }, [services, searchQuery]);

  const navigateToService = async (slug) => {
    if (!slug) {
      return;
    }
    await selectService(slug);
  };

  const runInternalAction = (link) => {
    const type = inferActionType(link);
    if (type === "LINK_INTERNO") {
      navigateToService(link.url);
      return;
    }
    setActiveInternalAction({ service: selectedService, action: link });
    scrollPortalToTop("acao");
  };

  const backToList = () => {
    window.history.pushState(null, "", "/");
    showServicesList();
  };

  if (adminRoute && !KEYCLOAK_ENABLED) {
    return (
      <>
        <PortalHeader />
        <main className="portal-services-shell portal-admin-route-state">
          <br-message
            message="A autenticação está temporariamente desabilitada neste ambiente. Habilite o Keycloak para acessar a gestão do portal."
            message-title="Administração indisponível."
            show-icon="true"
            state="warning"
          ></br-message>
          <GovBrButton onClick={() => window.location.assign("/")}>
            Voltar ao portal público
          </GovBrButton>
        </main>
      </>
    );
  }

  if (adminRoute && adminAuthError) {
    return (
      <>
        <PortalHeader />
        <main className="portal-services-shell portal-admin-route-state">
          <br-message
            message={adminAuthError}
            message-title="Não foi possível acessar a Administração."
            show-icon="true"
            state="danger"
          ></br-message>
          <GovBrButton onClick={() => window.location.reload()}>
            Tentar novamente
          </GovBrButton>
          <GovBrButton emphasis="tertiary" onClick={() => window.location.assign("/")}>
            Voltar ao portal público
          </GovBrButton>
        </main>
      </>
    );
  }

  if (adminRoute && !keycloak?.authenticated) {
    return (
      <>
        <PortalHeader />
        <LoadingState label="Redirecionando para autenticação..." />
      </>
    );
  }

  if (adminRoute && adminContextError) {
    return (
      <>
        <PortalHeader />
        <main className="portal-services-shell portal-admin-route-state">
          <br-message message={adminContextError} message-title="Não foi possível validar o acesso na aplicação." show-icon="true" state="danger"></br-message>
          <GovBrButton onClick={() => window.location.reload()}>Tentar novamente</GovBrButton>
          <GovBrButton emphasis="tertiary" onClick={() => window.location.assign("/")}>Portal público</GovBrButton>
        </main>
      </>
    );
  }

  if (adminRoute && (adminContextLoading || !adminContext)) {
    return (
      <>
        <PortalHeader />
        <LoadingState label="Consultando permissões na aplicação..." />
      </>
    );
  }

  if (accessAdminRoute && !permissions.canManageAccess) {
    return (
      <>
        <PortalHeader />
        <main className="portal-services-shell portal-admin-route-state">
          <br-message message="Seu usuário não possui a função de gestor de acessos." message-title="Acesso não autorizado." show-icon="true" state="warning"></br-message>
          <GovBrButton onClick={() => window.location.assign(ADMIN_PATH)}>Voltar à administração</GovBrButton>
        </main>
      </>
    );
  }

  if (accessAdminRoute) {
    return (
      <>
        <PortalHeader />
        <AccessManagement
          onBack={() => window.location.assign(ADMIN_PATH)}
          onPortalBack={() => window.location.assign("/")}
        />
      </>
    );
  }

  if (adminRoute && !permissions.canViewAdmin) {
    return (
      <>
        <PortalHeader />
        <main className="portal-services-shell portal-admin-route-state">
          <br-message
            message={permissions.canManageAccess
              ? "Seu perfil administra acessos, mas não possui acesso ao conteúdo dos serviços. Use a tela de gestão de acessos."
              : "Usuário autenticado, mas sem permissão cadastrada na aplicação para administrar serviços."}
            message-title="Acesso não autorizado."
            show-icon="true"
            state="warning"
          ></br-message>
          <GovBrButton onClick={() => window.location.assign("/")}>
            Voltar ao portal público
          </GovBrButton>
          {permissions.canManageAccess && <GovBrButton onClick={() => window.location.assign(ACCESS_ADMIN_PATH)}>Gerenciar acessos</GovBrButton>}
        </main>
      </>
    );
  }

  if (adminRoute) {
    return (
      <>
        <PortalHeader />
        <AdminDashboard
          permissions={permissions}
          onBack={() => window.location.assign("/")}
          onManageAccess={() => window.location.assign(ACCESS_ADMIN_PATH)}
        />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <PortalHeader />
        <LoadingState />
      </>
    );
  }

  if (error && !selectedService) {
    return (
      <>
        <PortalHeader />
        <ErrorState message={error} onRetry={() => {
          const slug = getServiceSlug();
          if (slug) {
            selectService(slug, { updateHistory: false });
          } else {
            loadServices();
          }
        }} />
      </>
    );
  }

  return (
    <>
      <PortalHeader titleRef={!selectedService ? listTitleRef : null} />
      <main className={selectedService ? "portal-services-shell" : "portal-services-page"} id="portal-servicos-app">
      {!selectedService && !detailLoading && (
        <div className="portal-services-shell portal-services-shell--public">
            <section className="portal-announcement">
              <br-message
                message={homeConfig.textoAviso || HOME_ANNOUNCEMENT_TEXT}
                show-icon="true"
                state="info"
              ></br-message>
            </section>

            <section className="portal-list-header portal-list-header--simple">
              <h2>Serviços disponíveis neste portal</h2>
            </section>

            {filteredServices.length > 0 ? (
              <ServicesList services={filteredServices} onSelect={selectService} />
            ) : (
              <div className="portal-state">Nenhum serviço disponível neste portal.</div>
            )}

            <section className="portal-services-cta" aria-labelledby="portal-services-cta-title">
              <div className="portal-services-cta__text">
                <span className="portal-services-cta__icon" aria-hidden="true">i</span>
                <div>
                  <h2 id="portal-services-cta-title">Não encontrou o serviço que procurava?</h2>
                  <p>Consulte a Carta de Serviços para acessar os demais serviços do TST.</p>
                </div>
              </div>
              <br-link button="true" href={CARTA_SERVICOS_URL} target="_blank" rel="noreferrer">
                Consultar Carta de Serviços
              </br-link>
            </section>

            <HomeOuvidoriaFooter />
          </div>
      )}

      {detailLoading && <LoadingState />}

      {selectedService && activeInternalAction && !detailLoading && (
        <InternalServiceAction
          service={activeInternalAction.service || selectedService}
          action={activeInternalAction.action}
          onBack={() => {
            setActiveInternalAction(null);
            scrollPortalToTop("detalhe");
          }}
        />
      )}

      {selectedService && !activeInternalAction && !detailLoading && (
        <ServiceDetail
          service={selectedService}
          onBack={backToList}
          titleRef={detailTitleRef}
          onNavigateService={navigateToService}
          onRunComponent={runInternalAction}
        />
      )}
      </main>
    </>
  );
}
